import { breakpoints } from '@measures/responsive'
import { FunctionComponent } from 'react'
import styled from 'styled-components'

const {
  desktop: mediaQueryDesktop,
  tablet: mediaQueryTablet,
  regularMobile: mediaQueryMobileRegular,
  smallMobile: mediaQueryMobileSmall,
} = breakpoints

interface ScribbleAlternateImage {
  filename: string
  height: number
  width: number
}

interface ScribbleImage extends ScribbleAlternateImage {
  alternateSizes?: ScribbleAlternateImage[]
}

interface ScribbleMeta {
  Images: ScribbleImage[]
}

interface ScribbleImageProps {
  imageUrl: string
  alt: string
  meta: ScribbleMeta
}

const StyledImg = styled.img`
  width: 100%;
  height: auto;
`

const imageObjectFromMeta = (imageUrl: string, meta: ScribbleMeta) => {
  return meta.Images.find((image) => image.filename === imageUrl)
}

const imageForScreen = (imageObject: ScribbleImage, maxWidth: number) => {
  const alternateSizes = imageObject.alternateSizes
  if (!alternateSizes) {
    return imageObject.filename
  }
  const sortedSizes = [...alternateSizes].sort(
    (imageA, imageB) => imageB.width - imageA.width
  )
  const targetImage = sortedSizes.find((image) => image.width <= maxWidth)
  return targetImage?.filename || imageObject.filename
}

const ScribbleImage: FunctionComponent<ScribbleImageProps> = ({
  imageUrl,
  alt,
  meta,
}) => {
  const plainImage = <StyledImg src={imageUrl} alt={alt} />

  if (!(meta?.Images?.length > 0)) {
    return plainImage
  }

  const imageObject = imageObjectFromMeta(imageUrl, meta)

  if (!imageObject) {
    return plainImage
  }

  // For tablet and desktop, 500 width is enough as the width of the scribble container is limited to 536px
  const pictureSourceDesktopAndTablet = imageForScreen(imageObject, 500)

  // For mobile, we can also assume that 400/300px is enough, as mobile screen widths are around these sizes
  const pictureSourceMobile = imageForScreen(imageObject, 400)
  const pictureSourceSmallMobile = imageForScreen(imageObject, 300)

  return (
    <picture>
      <source
        media={mediaQueryDesktop}
        srcSet={pictureSourceDesktopAndTablet}
      />
      <source media={mediaQueryTablet} srcSet={pictureSourceDesktopAndTablet} />
      <source media={mediaQueryMobileRegular} srcSet={pictureSourceMobile} />
      <source media={mediaQueryMobileSmall} srcSet={pictureSourceSmallMobile} />
      <StyledImg
        alt={alt}
        width={imageObject.width}
        height={imageObject.height}
        loading="lazy"
      />
    </picture>
  )
}

export default ScribbleImage
